import { Conference } from "@easycv/common/types/conference";
import {
  ContextConstants,
  ContextMethods,
  ContextState
} from "@easycv/common/types/context";
import { User } from "@easycv/common/types/user";
import { Backend } from "@easycv/common/types/util";
import dayjs from "dayjs";
import { escapeHtml } from "../../lib/frontendUtilities.js";
import { getLastPosition } from "../../lib/reportUtilities.js";
import { CCManage } from "../cclib/CCManage";
import { CCSetup } from "../cclib/CCSetup";
import { CCStructure } from "../cclib/CCStructure";
import { CCStyling, DefaultStyling } from "../cclib/CCStyling";
import { CCText, CCWrapper } from "../cclib/components/Form";
import { CCIcon } from "../cclib/components/UI";
import { demoCCStructure } from "./structure_demo_cc";

const dataPrefix = "demo_cc_";

const formUpdate = (cc: CCSetup) => {
  $(".careerYearPrev").toggleClass(
    "invisible",
    (cc.overviewState.yearIndex ?? 0) == 0
  );
  $(".careerYearNext").toggleClass(
    "invisible",
    (cc.overviewState.yearIndex ?? 0) >= cc.yearInfo.availableYears.length - 1
  );
  const priorYearData = cc.newDef.career[cc.viewYear - 1]?.data;
  if (priorYearData != undefined) {
    const prevFields = [
      "clinical_goals_future",
      "education_goals_future",
      "academic_goals_future",
      "other_goals_future",
      "burnout_current",
      "jobs_satisfaction_current"
    ];
    for (const field of prevFields) {
      if (Boolean(priorYearData[dataPrefix + field])) {
        cc.setValue(field + "_prev", priorYearData[dataPrefix + field]);
      }
    }
  }
};

export default {
  title: "EasyCV Demo Career Conference",
  tabTitle: "Annual Review",
  cvSetup: ({
    contextMethods,
    contextConstants,
    contextState,
    conference,
    backend
  }: {
    contextMethods: ContextMethods;
    contextConstants: ContextConstants;
    contextState: ContextState;
    conference: Conference;
    backend: Backend;
  }) => {
    const setup = new CCSetup(
      conference,
      backend,
      dataPrefix,
      contextMethods,
      contextConstants,
      contextState,
      formUpdate
    );
    // Get my mentor
    const cvOwnerId = contextConstants.cvOwner.id;
    const cvOwnersMentors: string[] = [];
    for (const mentor of Object.keys(conference.mentors)) {
      if (Boolean(conference.mentors[mentor][cvOwnerId])) {
        cvOwnersMentors.push(escapeHtml(contextConstants.mentors[mentor].name));
      }
    }

    setup.addButton(
      "default",
      CCIcon("arrow-left"),
      "header",
      "careerYearPrev",
      () => setup.changeYear(-1)
    );
    setup.addHTML(
      CCText("h4", "careerYear", setup.period, DefaultStyling.careerYear),
      {
        relative: "after",
        selector: ".careerYearPrev"
      }
    );
    setup.addButton(
      "default",
      CCIcon("arrow-right"),
      "header",
      "careerYearNext",
      () => setup.changeYear(1)
    );

    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Date Limited CV</span>",
      "header",
      "downloadDateLimited",
      () => setup.dateLimitedCVExport(contextState.customExportSettings)
    );
    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Full CV</span>",
      "header",
      "downloadFull",
      () => setup.fullCVExport(contextState.customExportSettings)
    );

    setup.addButton(
      "primary",
      CCIcon("print") + "<span>Print</span>",
      "header",
      "downloadPrint",
      () => setup.printExport("Title")
    );
    setup.addButton(
      "primary",
      CCIcon("download") + "<span>Download Review</span>",
      "header",
      "downloadDataSummary",
      () => {
        setup.conferenceExport(
          "demo_cc_v1_0.docx",
          "Annual Review",
          {},
          () => ""
        );
      }
    );

    const appraiserText = `Annual Review Appraiser${cvOwnersMentors.length > 1 ? "s" : ""}: ${cvOwnersMentors.join(",")}`;
    setup.addHTML(
      CCWrapper(
        CCText(
          "h4",
          "",
          appraiserText,
          CCStyling.fromObject({ width: "max-content" }, true)
        ),
        "",
        DefaultStyling.wrapper
      ),
      {
        relative: "after",
        selector: "#cc-header"
      }
    );

    const structure = new CCStructure(demoCCStructure);
    setup.generateFieldsFromStructure(structure, setup.ccRoot, false);

    const lastPos = getLastPosition(contextState.newDef) ?? {};

    setup.populateReadonlyValues([
      {
        yearAgnostic: true,
        key: "colleague_name",
        value: contextConstants.cvOwner.name
      },
      {
        yearAgnostic: true,
        key: "academic_rank",
        value: lastPos?.title ?? ""
      },
      {
        yearAgnostic: true,
        key: "time_in_position",
        value: lastPos?.years ?? ""
      },
      {
        yearAgnostic: true,
        key: "appraiser_name",
        value: cvOwnersMentors[0] ?? ""
      },
      {
        yearAgnostic: true,
        key: "appraisal_period",
        value:
          Boolean(setup.startDate) && Boolean(setup.endDate)
            ? `${dayjs(setup.startDate).format("M/D/YY")} - ${dayjs(setup.endDate).format("M/D/YY")}`
            : ""
      },
      {
        yearAgnostic: true,
        key: "department_name",
        value: contextState.newDef.bio?.dept ?? ""
      }
    ]);

    setup.appendStatusButtons();
    setup.update(setup.struct.structure);
    contextMethods.updateLayout();
  },
  manageSetup: (
    settingsEl: JQuery,
    { conference, updateConfDate, saveConference, hierarchyNames },
    user: User
  ) => {
    const ccManage = new CCManage(conference, saveConference, updateConfDate, {
      includeSections: {
        cutoff: true,
        mentees: true,
        goals: false,
        questions: false,
        mentors: true,
        reminders: false,
        years: true
      },
      sortBy: "mentor",
      verbage: {
        mentor: "Appraiser",
        mentee: "Mentee"
      }
    });
    ccManage.createSettingsElements(settingsEl);
    updateConfDate(conference);
    ccManage.createMentorsMenteesSection();
    ccManage.createAwesomplete();
    ccManage.hookMessageButton(user);
    ccManage.populateMenteesSection(hierarchyNames);
  }
};
